export type KeyFingerprintRecord = {
  name: string;
  scope: string;
  algorithm: string;
  fingerprintLabel: string;
  fingerprint: string;
  source: string;
  note?: string;
};

export type KeyFingerprintSection = {
  id: string;
  title: string;
  description: string;
  verifyCommand?: string;
  records: KeyFingerprintRecord[];
};

export const keyFingerprintSections: KeyFingerprintSection[] = [
  {
    id: "ssh-signing",
    title: "Git SSH signing keys",
    description:
      "Tracked commit-signing identities sourced from config/git-identities/*.pub.",
    verifyCommand: "ssh-keygen -lf config/git-identities/<identity>.pub",
    records: [
      {
        name: "founder",
        scope: "founder@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "zGKs3AwSgZ5GprYK12Atfxw30bVRmJbxChJoyV5En2k",
        source: "config/git-identities/founder.pub",
      },
      {
        name: "founder-legacy",
        scope: "founder@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "tbm5C0Q6+g4LaCRNRY+D2aknhyU1ya4MIH7/AFcNg8A",
        source: "config/git-identities/founder-legacy.pub",
        note: "Legacy key retained for historical verification only.",
      },
      {
        name: "forge",
        scope: "forge@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "gNOsvO0KOu7TaSWOBDwGBwsiExR7kauwDr9O2+IswJQ",
        source: "config/git-identities/forge.pub",
      },
      {
        name: "runner",
        scope: "runner@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "Cfeu3Yhsf87wxRbqENQZTELinsdHdM1BfcynHIM/2yY",
        source: "config/git-identities/runner.pub",
      },
      {
        name: "release-runner",
        scope: "release-runner@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "ktJxuNbXhEllUjUwVY26GJG2nxN2/eTzK2muN0eZpl4",
        source: "config/git-identities/release-runner.pub",
      },
      {
        name: "agent",
        scope: "agent@key.store",
        algorithm: "ED25519 (SSH)",
        fingerprintLabel: "SHA256",
        fingerprint: "SYIB+PN3EC3zqFTSlJTuqY1nqja6l2ap/270DjeRnWk",
        source: "config/git-identities/agent.pub",
      },
    ],
  },
  {
    id: "sparkle",
    title: "Sparkle update signing key",
    description: "Public key used by Sparkle to verify macOS Developer ID update feeds.",
    verifyCommand:
      "python3 - <<'PY'\nimport base64, hashlib\nraw = base64.b64decode(open('Apple/Sparkle/PublicKeys/key.store.ed25519', 'rb').read().strip())\nprint(base64.b64encode(hashlib.sha256(raw).digest()).decode())\nPY",
    records: [
      {
        name: "Sparkle Ed25519 public key",
        scope: "macOS Sparkle feed",
        algorithm: "Ed25519 (Sparkle)",
        fingerprintLabel: "Key SHA256 (base64)",
        fingerprint: "04NFRzKD7lt/v3FAd+GOmkEKJ9vpY+VSTGMVORpAWR4=",
        source: "Apple/Sparkle/PublicKeys/key.store.ed25519",
        note: "Sparkle public key: NkmI/LtsxFij38huSMav/PRcmsfEd2edjxuCUI8Jv2I=",
      },
    ],
  },
  {
    id: "android",
    title: "Android + F-Droid signing certificate",
    description:
      "APK signing certificate imported into the PKCS#11 release-signing backends for Play and F-Droid releases.",
    verifyCommand:
      "openssl pkcs12 -in Android/key-store.jks -nokeys -passin pass:\"$STORE_PASSWORD\" | openssl x509 -noout -fingerprint -sha256",
    records: [
      {
        name: "Android upload/release cert",
        scope: "Google Play + F-Droid",
        algorithm: "X.509 (RSA 4096, SHA256withRSA)",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "00:5F:4A:68:EF:1C:B6:69:34:EF:D8:45:B0:4A:AC:26:53:79:9E:F0:0B:09:48:13:D5:C4:7B:B2:12:EA:6B:E6",
        source: "CloudHSM / Google KMS imported release key",
        note: "Subject CN=key.store Upload, valid 2026-02-09 to 2036-02-07.",
      },
    ],
  },
  {
    id: "apple-codesign",
    title: "Apple code signing certificates",
    description:
      "Apple code signing certificates paired with imported PKCS#11 release keys.",
    verifyCommand:
      "openssl pkcs12 -in <developer-cert.p12> -nokeys -passin pass:\"$CERT_PASSWORD\" | openssl x509 -noout -fingerprint -sha256",
    records: [
      {
        name: "Developer ID Application",
        scope: "macOS outside App Store",
        algorithm: "X.509 (Apple Developer ID)",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "5C:CF:04:BB:D1:66:A4:41:90:B8:29:7A:20:51:77:6D:A2:33:BB:7D:0C:B1:D8:1D:F4:6F:26:83:B6:06:65:F9",
        source: "CloudHSM / Google KMS imported Apple key",
      },
      {
        name: "Apple Distribution",
        scope: "App Store distribution",
        algorithm: "X.509 (Apple Distribution)",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "EB:49:9C:E0:15:38:F7:71:F4:AE:C3:9C:58:FB:EC:51:9A:F5:B9:64:7F:17:FD:D0:15:AC:DC:F8:58:B1:D0:22",
        source: "CloudHSM / Google KMS imported Apple key",
      },
      {
        name: "Apple Development",
        scope: "Apple development signing",
        algorithm: "X.509 (Apple Development)",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "CA:F2:40:C8:5D:BB:88:D4:EE:86:A1:2A:A4:B9:EA:6D:AA:70:0B:35:AE:91:84:D1:B5:FD:E2:9D:64:3F:0E:18",
        source: "Developer-only local certificate",
      },
    ],
  },
  {
    id: "tls",
    title: "Live TLS (SSL) certificates",
    description:
      "Observed leaf certificate fingerprints for active key.store domains on 2026-02-24 UTC.",
    verifyCommand:
      "openssl s_client -connect <host>:443 -servername <host> </dev/null | openssl x509 -noout -fingerprint -sha256",
    records: [
      {
        name: "key.store",
        scope: "Primary site",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "AC:3D:E1:08:88:95:B7:E0:05:4F:F0:BA:E0:6F:EF:D3:C0:E4:BD:16:D1:86:B5:8F:FA:30:1C:A3:16:60:58:E1",
        source: "live endpoint",
        note: "SPKI SHA256(base64): SFYtoyWywW6Xg13i189cmhFLfjELfDVb5HFboJL+0wM=",
      },
      {
        name: "graphs.key.store",
        scope: "Metrics UI",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "0C:69:E6:6E:E6:05:B1:8C:CD:CA:99:04:30:4E:6A:AB:2F:12:BE:8E:7F:95:5E:52:C1:26:FE:BE:A7:77:F0:DA",
        source: "live endpoint",
        note: "SPKI SHA256(base64): ICkD0GGIHAzizILe4bCJqQmBPkHB+oNhafXcioihi9s=",
      },
      {
        name: "traces.key.store",
        scope: "Trace UI",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "E7:F3:6C:64:07:67:20:6F:35:B1:BB:D9:68:91:6E:DE:6D:3E:BF:8E:20:5D:6B:C7:7A:F0:39:DE:D7:3F:9A:3D",
        source: "live endpoint",
        note: "SPKI SHA256(base64): yuufEUbqCUB0YBe1ZFg6m0LMXbMSCpTlW1bYv42SsXI=",
      },
      {
        name: "blob.key.store",
        scope: "Object storage endpoint",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "35:D3:74:C2:9A:D8:0E:2B:4D:CD:94:67:D1:14:94:35:3B:42:A0:16:6E:1B:8F:5D:F5:C7:8C:FA:A1:35:66:BB",
        source: "live endpoint",
        note: "SPKI SHA256(base64): Q6Y3TkbUPvYhkCF+3lfUkSfLMI4kIOY3RQ52z2YufDI=",
      },
      {
        name: "oci.key.store",
        scope: "Container registry endpoint",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "29:A5:86:CB:17:E2:27:8D:79:62:9C:C1:B4:2D:B7:7F:65:C2:50:4D:38:05:2D:5E:C7:2D:D6:91:78:18:26:60",
        source: "live endpoint",
        note: "SPKI SHA256(base64): 07yKb3ccx52aINTiC/qpIhIQcFX7LhGj3gNe4dBtC/c=",
      },
      {
        name: "mail.key.store",
        scope: "Mail UI endpoint",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "27:DC:9F:F6:68:23:A3:37:82:3E:C6:5A:FB:21:AE:B2:F2:BD:C4:FE:39:9A:B4:EC:82:79:D5:AC:D2:F9:CA:BD",
        source: "live endpoint",
        note: "SPKI SHA256(base64): sOvqqVk2JRCVefb4inYqHY1/IlySOsaEF/WdBvqwbI0=",
      },
      {
        name: "idp.key.store",
        scope: "OIDC endpoint",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "CD:79:DB:BD:47:0A:F9:F9:03:66:E2:E9:C9:A6:46:1E:C1:BB:11:7D:28:3E:6E:66:7A:A7:BD:BD:B0:15:D9:97",
        source: "live endpoint",
        note: "SPKI SHA256(base64): z6lLY5U0U0yfcVShP0i0fz61kEO3+Cdg3BnNKrSNmNw=",
      },
      {
        name: "todo.key.store",
        scope: "todo endpoint",
        algorithm: "X.509 TLS leaf cert",
        fingerprintLabel: "Cert SHA256",
        fingerprint: "62:FE:0A:95:C3:B4:0C:DA:00:C1:1C:7D:9A:37:F5:BD:7D:95:DE:7A:0D:49:88:5D:E3:E3:EA:AD:3D:8A:F9:1A",
        source: "live endpoint",
        note: "SPKI SHA256(base64): 38cju4SqLWJBJ7hg+NAxwlNzVUWV9Tr9VTuYwtwazRY=",
      },
      {
        name: "fdroid.key.store",
        scope: "F-Droid domain",
        algorithm: "DNS/TLS status",
        fingerprintLabel: "Status",
        fingerprint: "unresolved",
        source: "live DNS lookup",
        note: "Host did not resolve at observation time.",
      },
    ],
  },
];
